import dagre from 'dagre';
import {Edge, Node, NodeOrigin} from 'reactflow';

import {assertAndContinue} from 'Asserts';

import {DimensionsForLayout} from './calculateDimensionsForLayout';
import {unhideFlowchartElementAfterLayout} from './hideFlowchartElementBeforeLayoutUtils';
import {snapPositionToGrid} from './snapPositionToGrid';
import translateNodeOrigin from './translateNodeOrigin';

// Dagre lays out nodes by the _center_ of each node.
const DAGRE_NODE_ORIGIN: NodeOrigin = [0.5, 0.5];

/**
 * Arrange nodes using the `dagre` library.
 *
 * This algorithm only computed positions for nodes in the layout. No data for edges and their paths are computed.
 *
 * Returns positions for the _center_ of each node (note: `reactflow` uses `nodeOrigin={[0,0]}` by
 * default, which treats the positions as the _top left_ of each node. To correct for this use
 * `nodeOrigin={[0, 0]}` in `dimensions`).
 *
 * This layout algorithm does not guarantee nodes do not overlap edge labels.
 *
 * @param nodes The nodes to layout.
 * @param edges The edges to layout.
 * @param dimensions The dimensions of the nodes and edges.
 * @returns The nodes and edges (nodes have updated positions).
 */
const dagreLayout = <N extends Node, E extends Edge>(
  {nodes, edges}: {nodes: Array<N>; edges: Array<E>},
  dimensions: DimensionsForLayout
): Promise<{nodes: Array<N>; edges: Array<E>}> => {
  const dagreGraph = new dagre.graphlib.Graph();

  dagreGraph.setGraph({
    rankdir: 'LR', // 'LR' = arrange left to right
    ranksep: dimensions.horizontalSpaceBetweenNodes,
    nodesep: dimensions.verticalSpaceBetweenNodes,
  });
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((node) => {
    assertAndContinue(dimensions.nodes[node.id] != null, 'Node is missing dimensions needed for layout');
    const {height, width} = dimensions.nodes[node.id] ?? {height: 0, width: 0};
    dagreGraph.setNode(node.id, {height, width});
  });
  edges.forEach((edge) => {
    assertAndContinue(dimensions.edges[edge.id] != null, 'Edge is missing dimensions needed for layout');
    const {height, width} = dimensions.edges[edge.id] ?? {height: 0, width: 0};
    dagreGraph.setEdge(edge.source, edge.target, {height, width});
  });

  dagre.layout(dagreGraph);

  // Apply calculated positions, and un-hide any elements that were temporarily hidden.
  return Promise.resolve({
    nodes: nodes.map((node) => {
      const dagrePosition = dagreGraph.node(node.id);
      let dagrePositionX = dagrePosition.x;
      let dagrePositionY = dagrePosition.y;
      if (dimensions.snapToGrid != null) {
        const snappedPosition = snapPositionToGrid(dagrePosition, dimensions.snapToGrid);
        dagrePositionX = snappedPosition.x;
        dagrePositionY = snappedPosition.y;
      }
      return unhideFlowchartElementAfterLayout({
        ...node,
        position: translateNodeOrigin({
          sourceNodeOrigin: DAGRE_NODE_ORIGIN,
          sourcePosition: {x: dagrePositionX, y: dagrePositionY},
          targetNodeOrigin: dimensions.nodeOrigin,
          nodeHeight: node.height ?? 0,
          nodeWidth: node.width ?? 0,
        }),
      });
    }),
    edges: edges.map((edge) => unhideFlowchartElementAfterLayout(edge)),
  });
};

export default dagreLayout;
